/**
 * Admin API Service
 * GNU Board 관리자 기능을 위한 API 클라이언트
 */

import axios, { AxiosInstance } from "axios";

// API 응답 타입 정의
export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
}

export interface User {
  mb_id: string;
  mb_name: string;
  mb_nick: string;
  mb_email: string;
  mb_level: number;
  is_admin: string;
}

export interface LoginResponse {
  token: string;
  user: User;
}

export interface PostData {
  wr_id?: number;
  ca_name?: string;
  wr_subject: string;
  wr_content: string;
  wr_link1?: string;
  wr_link2?: string;
  notice?: boolean;
}

export interface FileInfo {
  bf_no?: number;
  source: string;
  file: string;
  size: number;
  download?: number;
  width?: number;
  height?: number;
  url: string;
  download_url?: string;
  datetime?: string;
}

class AdminApi {
  private api: AxiosInstance;
  private token: string | null = null;

  constructor() {
    // API URL 설정
    // 개발 환경에서는 proxy를 통해 요청하므로 상대 경로 사용
    const baseURL =
      process.env.REACT_APP_API_BASE_URL ||
      (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"
        ? "/api" // 로컬 개발 환경 - proxy 사용
        : "/api"); // 프로덕션 환경에서는 상대 경로 사용

    this.api = axios.create({
      baseURL,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true, // 쿠키 전송
    });

    // 토큰 로드
    const savedToken = localStorage.getItem("admin_token");
    if (savedToken) {
      this.setToken(savedToken);
    }

    // 요청 인터셉터
    this.api.interceptors.request.use(
      (config) => {
        if (this.token && config.headers) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }

        // FormData인 경우 Content-Type을 삭제하여 브라우저가 자동 설정하도록 함
        if (config.data instanceof FormData) {
          if (config.headers) {
            delete config.headers["Content-Type"];
          }
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // 응답 인터셉터
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        // 401 에러 시 토큰 제거
        // 리다이렉트는 AdminProtectedRoute에서 자동으로 처리됨
        if (error.response?.status === 401) {
          this.clearToken();
        }
        return Promise.reject(error);
      }
    );
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem("admin_token", token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem("admin_token");

    // 토큰 제거 이벤트 발생 (AdminAuthContext에서 감지)
    window.dispatchEvent(new CustomEvent('admin_token_cleared'));
  }

  // 인증 관련
  async login(mb_id: string, mb_password: string): Promise<LoginResponse> {
    const response = await this.api.post<ApiResponse<LoginResponse>>(
      "/admin/auth.php?action=login",
      { mb_id, mb_password }
    );

    if (response.data.success && response.data.data) {
      this.setToken(response.data.data.token);
      return response.data.data;
    }

    throw new Error(response.data.message || "로그인 실패");
  }

  async verify(): Promise<User> {
    const response = await this.api.get<ApiResponse<{ user: User }>>(
      "/admin/auth.php?action=verify"
    );

    if (response.data.success && response.data.data) {
      return response.data.data.user;
    }

    throw new Error(response.data.message || "인증 실패");
  }

  async logout(): Promise<void> {
    await this.api.post("/admin/auth.php?action=logout");
    this.clearToken();
  }

  // 게시판 관리
  async createPost(
    bo_table: string,
    postData: PostData,
    files?: File[]
  ): Promise<any> {
    const formData = new FormData();

    // bo_table 추가 (필수)
    formData.append("bo_table", bo_table);
    formData.append("action", "write");

    // 게시글 데이터 추가
    Object.entries(postData).forEach(([key, value]) => {
      if (key === "notice") {
        formData.append(key, value ? "1" : "0");
      } else if (value !== undefined) {
        formData.append(key, String(value));
      }
    });

    // 파일 추가
    if (files && files.length > 0) {
      console.log(`Adding ${files.length} files to FormData`);
      files.forEach((file, index) => {
        formData.append("bf_file[]", file);
        console.log(`File ${index}: ${file.name}, ${file.size} bytes`);
      });
    }

    for (let pair of formData.entries()) {
      if (pair[1] instanceof File) {
        console.log(pair[0], "(File)", pair[1].name, pair[1].size);
      } else {
        console.log(pair[0], pair[1]);
      }
    }

    const response = await this.api.post<ApiResponse>(
      `/admin/board.php`,
      formData
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || "게시글 작성 실패");
  }

  async updatePost(bo_table: string, postData: PostData): Promise<any> {
    const formData = new FormData();

    // bo_table 추가 (필수)
    formData.append("bo_table", bo_table);
    formData.append("action", "update");

    // 게시글 데이터 추가
    Object.entries(postData).forEach(([key, value]) => {
      if (key === "notice") {
        formData.append(key, value ? "1" : "0");
      } else if (value !== undefined) {
        formData.append(key, String(value));
      }
    });

    const response = await this.api.post<ApiResponse>(
      `/admin/board.php`,
      formData
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || "게시글 수정 실패");
  }

  async deletePost(bo_table: string, wr_id: number): Promise<any> {
    const formData = new FormData();
    formData.append("bo_table", bo_table);
    formData.append("action", "delete");
    formData.append("wr_id", String(wr_id));

    const response = await this.api.post<ApiResponse>(
      `/admin/board.php`,
      formData
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || "게시글 삭제 실패");
  }

  // 댓글(답변) 작성
  async createComment(bo_table: string, wr_id: number, content: string): Promise<any> {
    const formData = new FormData();
    formData.append("bo_table", bo_table);
    formData.append("action", "comment");
    formData.append("wr_id", String(wr_id));
    formData.append("wr_content", content);

    const response = await this.api.post<ApiResponse>(
      `/admin/board.php`,
      formData
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || "답변 작성 실패");
  }

  // 댓글(답변) 삭제
  async deleteComment(bo_table: string, comment_id: number): Promise<any> {
    const formData = new FormData();
    formData.append("bo_table", bo_table);
    formData.append("action", "delete_comment");
    formData.append("comment_id", String(comment_id));

    const response = await this.api.post<ApiResponse>(
      `/admin/board.php`,
      formData
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || "답변 삭제 실패");
  }

  // 파일 관리
  async uploadFiles(
    bo_table: string,
    wr_id: number | null,
    files: File[]
  ): Promise<FileInfo[]> {
    const formData = new FormData();
    formData.append("bo_table", bo_table);
    formData.append("action", "upload");

    if (wr_id) {
      formData.append("wr_id", String(wr_id));
    }

    files.forEach((file) => {
      formData.append("files[]", file);
    });

    const response = await this.api.post<ApiResponse<{ files: FileInfo[] }>>(
      "/admin/file.php",
      formData
    );

    if (response.data.success && response.data.data) {
      return response.data.data.files;
    }

    throw new Error(response.data.message || "파일 업로드 실패");
  }

  async deleteFile(
    bo_table: string,
    wr_id: number,
    bf_no: number
  ): Promise<any> {
    const formData = new FormData();
    formData.append("bo_table", bo_table);
    formData.append("action", "delete");
    formData.append("wr_id", String(wr_id));
    formData.append("bf_no", String(bf_no));

    const response = await this.api.post<ApiResponse>(
      "/admin/file.php",
      formData
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || "파일 삭제 실패");
  }

  async getFiles(bo_table: string, wr_id: number): Promise<FileInfo[]> {
    const response = await this.api.get<ApiResponse<{ files: FileInfo[] }>>(
      `/admin/file.php?action=list&bo_table=${bo_table}&wr_id=${wr_id}`
    );

    if (response.data.success && response.data.data) {
      return response.data.data.files;
    }

    throw new Error(response.data.message || "파일 목록 조회 실패");
  }

  // 에디터 이미지 업로드
  async uploadEditorImage(imageFile: File): Promise<string> {
    const formData = new FormData();
    formData.append("image", imageFile);

    const response = await this.api.post<
      ApiResponse<{
        url: string;
        filename: string;
        original_name: string;
        size: number;
        width: number;
        height: number;
        path: string;
      }>
    >("/editor/upload.php?response_type=json", formData);

    if (response.data.success && response.data.data) {
      return response.data.data.url;
    }

    throw new Error(response.data.message || "이미지 업로드 실패");
  }

  // 기존 게시판 API 사용 (목록 조회 등)
  async getPostList(bo_table: string, page = 1, limit = 10, search = "") {
    const response = await this.api.get<ApiResponse>(
      `/board.php?bo_table=${bo_table}&action=list&page=${page}&limit=${limit}&search=${search}`
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || "게시글 목록 조회 실패");
  }

  async getPost(bo_table: string, wr_id: number) {
    const response = await this.api.get<ApiResponse>(
      `/board.php?bo_table=${bo_table}&action=view&wr_id=${wr_id}`
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || "게시글 조회 실패");
  }
}

export default new AdminApi();
